import type { EntryCollection, EntrySkeletonType } from 'contentful'
import { ALLOWED_CONTENT_TYPES, PreviewConfig } from '~/types/ContentfulPreview'

export const useContentfulPreview = () => {
  const { locale } = useNuxtApp().$i18n
  const { getLocalePrefix } = useSiteUrl()
  const localePrefix = getLocalePrefix()
  const route = useRoute()
  const currentContentType = route.path.replace(/.*\//, '')
  const queryString = new URLSearchParams(route.query as Record<string, string>).toString()
  const isMyBagContentType = /\/preview\/my_bag/.test(route.path)

  const {
    previewId,
    previewEnv,
    previewPersonalizationTag,
    previewMarket,
    previewLocale,
    previewByDate,
    productId,
    siteDate,
    customerGroup,
  } = route.query
  const previewLocaleKey = (previewLocale as string) || locale.value
  const previewConfig: PreviewConfig & PromotionPreviewConfig = {
    previewId: previewId as string | undefined,
    previewEnv: previewEnv as string | undefined,
    previewPersonalizationTag: previewPersonalizationTag as string | undefined,
    // AC6: preview mirrors production, and previewLocale already implies the market — so derive
    // it rather than make Trade remember a second param. previewMarket overrides for testing.
    previewMarket:
      (isValidMarket(previewMarket) ? previewMarket : undefined) || localeConfig[previewLocaleKey]?.country,
    previewLocale: localeConfig[previewLocaleKey].code as CntflLocales | undefined,
    previewByDate: previewByDate as string | undefined,
    productId: productId as string | undefined,
    siteDate: siteDate as string | undefined,
    customerGroup: customerGroup as string | undefined,
  }
  const isPreviewEnabled = !!(previewId || previewEnv)
  const isPromotionPreview = !!(siteDate || customerGroup)

  const sanitizeQueryForPreview = (queryParams: ContentfulParams & ContentfulPreviewParams) => {
    if (!isPreviewEnabled || isValidContentType()) return queryParams

    // Remove 'sys.publishedVersion[exists]' to include unpublished entries in preview mode
    const { 'sys.publishedVersion[exists]': _publishedVersionExists, ...baseQuery } = queryParams

    // For slug content type, also remove content_type and fields.slug
    const cleanedQuery =
      queryParams.content_type === 'slug'
        ? (() => {
            const { content_type: _contentType, 'fields.slug': _fieldsSlug, ...rest } = baseQuery
            return rest
          })()
        : baseQuery

    return {
      ...cleanedQuery,
      ...(previewLocale && { locale: previewLocale }),
      ...(previewEnv && { previewEnv }),
      ...(previewId && { previewId }),
    } as ContentfulParams & ContentfulPreviewParams
  }

  const buildQueryParams = () =>
    ({
      previewEnv: previewEnv as string | undefined,
      ...(currentContentType && { previewId: previewId as string | undefined }),
      ...(previewLocale && { locale: previewLocale as string | undefined }),
    }) as any

  const isValidContentType = (contentType?: string): contentType is (typeof ALLOWED_CONTENT_TYPES)[number] => {
    const typeToCheck = contentType || currentContentType
    const normalized = toSnakeCase(typeToCheck) as (typeof ALLOWED_CONTENT_TYPES)[number]
    return ALLOWED_CONTENT_TYPES.includes(normalized)
  }

  const resolvePathFromSlug = (slug: string) => {
    if (['blog_article', 'connected_content_pillar'].includes(currentContentType))
      return `${localePrefix}/connected/${slug}?${queryString}`
    if (currentContentType === 'product_listing_page')
      return `${localePrefix}/${slug.split('_').join('/')}?${queryString}`
    return `${localePrefix}/${slug}?${queryString}`.replace(/^\/+/, '/')
  }

  const resolvePathFromProductId = (productId: string) => {
    return `${localePrefix}/p/jewellery-${productId}.html?${queryString}`
  }

  const resolvePathFromMyBag = () => {
    return `${localePrefix}/my-bag?${queryString}`
  }

  const getSlugFromEntries = (entries: EntryCollection<EntrySkeletonType, undefined, string>) => {
    const slug = getNestedValue(entries, ['items:0', 'fields', 'slug'])
    const categoryId = getNestedValue(entries, ['items:0', 'fields', 'categoryId'])
    if (!slug && !categoryId) {
      return getNestedValue(entries, ['items:0', 'fields', 'supportingData', 'home']) ? '/' : undefined
    }
    return currentContentType === 'product_listing_page'
      ? categoryId
      : currentContentType === 'my_bag'
        ? 'my-bag'
        : slug
  }

  const getPreviewContentType = (
    entries: EntryCollection<EntrySkeletonType, undefined, string> | undefined,
    previewConfig: PreviewConfig,
  ): string =>
    previewConfig.productId
      ? 'pdpPreferences'
      : getNestedItem(entries, ['items:sys.id'], previewConfig.previewId)?.sys?.contentType?.sys?.id ||
        getNestedValue(entries, ['items:0', 'sys', 'contentType', 'sys', 'id'])

  const getPreviewContent = async (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => {
    // Locale and previewId are required in preview mode
    if (!previewConfig.previewLocale && !previewConfig.previewId) return entries

    // Define available preview filters here
    const previewFilters: PreviewFilters = {
      contentPage: usePreviewContentPageFilter,
      slug: usePreviewContentPageFilter,
      blogArticle: usePreviewBlogArticleFilter,
      connectedContentPillar: usePreviewConnectedPillarFilter,
      cartPreferences: usePreviewMyBagFilter,
      pdpPreferences: usePreviewProductDetailsPageFilter,
      productListingPage: usePreviewProductListingPageFilter,
    }

    // Determine content type with fallback logic
    const contentType = getPreviewContentType(entries, previewConfig)

    // Validate content type is supported
    if (!contentType || !Object.prototype.hasOwnProperty.call(previewFilters, contentType)) {
      return entries
    }

    const {
      filterByLocale,
      filterByPreviewId,
      filterByPersonalizationTag,
      filterByMarket,
      filterByPreviewDate,
      transformContent,
    } = previewFilters[contentType]()

    let newEntries = { ...entries }
    newEntries = transformContent ? transformContent(newEntries, previewConfig) : newEntries
    newEntries = filterByLocale ? filterByLocale(newEntries, previewConfig) : newEntries
    newEntries = filterByPreviewId ? filterByPreviewId(newEntries, previewConfig) : newEntries

    if (!!previewConfig.previewPersonalizationTag) {
      newEntries = filterByPersonalizationTag ? filterByPersonalizationTag(newEntries, previewConfig) : newEntries
    }

    if (!!previewConfig.previewMarket) {
      newEntries = filterByMarket ? filterByMarket(newEntries, previewConfig) : newEntries
    }

    if (!!previewConfig.previewByDate) {
      newEntries = filterByPreviewDate ? await filterByPreviewDate(newEntries, previewConfig) : newEntries
    }

    return newEntries
  }

  return {
    // preview state
    isPreviewEnabled,
    isPromotionPreview,
    contentType: isValidContentType() ? currentContentType : undefined,
    isMyBagContentType,
    getPreviewContentType,

    // preview parameters
    ...previewConfig,

    // config and utilities
    previewConfig,
    sanitizeQueryForPreview,
    isValidContentType,
    buildQueryParams,

    // path resolution
    resolvePathFromSlug,
    resolvePathFromProductId,
    resolvePathFromMyBag,

    // content filtering
    getSlugFromEntries,
    getPreviewContent,
  }
}
